import { InputLabel } from '@mui/material';
import { type ChangeEvent } from 'react';
import { KeywordMultiSelectDropdown } from '#components/Filters/KeywordMultiSelectDropdown/index.tsx';
import { areMarks, areOptions } from '#components/Filters/helpers/otherHelpers.ts';
import { type FiltersConfig } from '#types.ts';
import { InputLabelWithCheckbox } from '../../Form/InputLabelWithCheckbox/index.tsx';
import { type Mark } from '../../Form/RangeSlider/types.ts';
import { CertificationRangeSlider } from '../CertificationRangeSlider/index.tsx';
import { OuterQuery as CompanyMultiSelectDropdown } from '../CompanyMultiSelectDropdown/index.ts';
import { GenreMultiSelectDropdown } from '../GenreMultiSelectDropdown/index.tsx';
import { OuterQuery as PersonMultiSelectDropdown } from '../PersonMultiSelectDropdown/index.ts';
import { ReleaseTypeMultiSelectDropdown } from '../ReleaseTypeMultiSelectDropdown/index.tsx';
import { ReleaseYearRangeSlider } from '../ReleaseYearRangeSlider/index.tsx';
import { RuntimeRangeSlider } from '../RuntimeRangeSlider/index.tsx';
import { VoteAverageRangeSlider } from '../VoteAverageRangeSlider/index.tsx';
import { type MovieFormikValues, type Option, type UseFormik } from '../types.ts';

export type Props = {
  config: FiltersConfig;
  filterOptions: Record<string, Mark[] | Option[]>;
  formik: UseFormik<MovieFormikValues>;
  onRangeSliderToggle: (
    event: ChangeEvent<HTMLInputElement>,
    name: keyof MovieFormikValues,
    checked: boolean,
    defaultValue: number[],
  ) => void;
};

export const MovieFilters = ({ config, filterOptions, formik, onRangeSliderToggle }: Props) => {
  const { rangeSliders } = config;

  return (
    <>
      <InputLabelWithCheckbox
        checked={!rangeSliders?.certifications?.disabled || false}
        id="certifications-filter-label"
        onChange={(e, checked) => {
          onRangeSliderToggle(e, 'certifications', checked, [1, 1]);
        }}
        sx={{ marginTop: 0 }}
      >
        Certifications
      </InputLabelWithCheckbox>
      {areMarks(filterOptions.certifications) ? (
        <CertificationRangeSlider
          ariaLabelledBy="certifications-filter-label"
          certifications={filterOptions.certifications}
          disabled={rangeSliders?.certifications?.disabled ?? false}
          name="certifications"
          onChange={formik.handleChange}
          value={formik.values.certifications}
        />
      ) : undefined}
      <InputLabelWithCheckbox
        checked={!rangeSliders?.releaseYears?.disabled || false}
        id="release-year-filter-label"
        onChange={(e, checked) => {
          onRangeSliderToggle(e, 'releaseYears', checked, [new Date().getFullYear() - 10, new Date().getFullYear()]);
        }}
      >
        Release year
      </InputLabelWithCheckbox>
      <ReleaseYearRangeSlider
        ariaLabelledBy="release-years-filter-label"
        disabled={rangeSliders?.releaseYears?.disabled ?? false}
        name="releaseYears"
        onChange={formik.handleChange}
        value={formik.values.releaseYears}
      />
      <InputLabelWithCheckbox
        checked={!rangeSliders?.voteAverages?.disabled || false}
        id="vote-averages-filter-label"
        onChange={(e, checked) => {
          onRangeSliderToggle(e, 'voteAverages', checked, [0, 10]);
        }}
      >
        Vote average
      </InputLabelWithCheckbox>
      <VoteAverageRangeSlider
        ariaLabelledBy="vote-averages-filter-label"
        disabled={rangeSliders?.voteAverages?.disabled ?? false}
        name="voteAverages"
        onChange={formik.handleChange}
        value={formik.values.voteAverages}
      />
      <InputLabelWithCheckbox
        checked={!rangeSliders?.runtimes?.disabled || false}
        id="runtimes-filter-label"
        onChange={(e, checked) => {
          onRangeSliderToggle(e, 'runtimes', checked, [90, 150]);
        }}
      >
        Runtime (hrs)
      </InputLabelWithCheckbox>
      <RuntimeRangeSlider
        ariaLabelledBy="runtimes-filter-label"
        disabled={rangeSliders?.runtimes?.disabled ?? false}
        name="runtimes"
        onChange={formik.handleChange}
        value={formik.values.runtimes}
      />
      <InputLabel
        id="cast-filter-label"
        sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
      >
        With cast
      </InputLabel>
      <PersonMultiSelectDropdown
        ariaLabelledBy="cast-filter-label"
        name="cast"
        setFieldValue={formik.setFieldValue}
        value={formik.values.cast}
      />
      <InputLabel
        id="crew-filter-label"
        sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
      >
        With crew
      </InputLabel>
      <PersonMultiSelectDropdown
        ariaLabelledBy="crew-filter-label"
        name="crew"
        setFieldValue={formik.setFieldValue}
        value={formik.values.crew}
      />
      <InputLabel
        id="companies-filter-label"
        sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
      >
        With company
      </InputLabel>
      <CompanyMultiSelectDropdown
        ariaLabelledBy="companies-filter-label"
        name="companies"
        setFieldValue={formik.setFieldValue}
        value={formik.values.companies}
      />
      {areOptions(filterOptions.genres) ? (
        <>
          <InputLabel
            id="with-genres-filter-label"
            sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
          >
            With genre
          </InputLabel>
          <GenreMultiSelectDropdown
            ariaLabelledBy="with-genres-filter-label"
            name="withGenres"
            options={filterOptions.genres}
            setFieldValue={formik.setFieldValue}
            value={formik.values.withGenres}
          />
          <InputLabel
            id="without-genres-filter-label"
            sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
          >
            Without genre
          </InputLabel>
          <GenreMultiSelectDropdown
            ariaLabelledBy="without-genres-filter-label"
            name="withoutGenres"
            options={filterOptions.genres}
            setFieldValue={formik.setFieldValue}
            value={formik.values.withoutGenres}
          />
        </>
      ) : undefined}
      {areOptions(filterOptions.keywords) ? (
        <>
          <InputLabel
            id="with-keywords-filter-label"
            sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
          >
            With keyword
          </InputLabel>
          <KeywordMultiSelectDropdown
            ariaLabelledBy="with-keywords-filter-label"
            name="withKeywords"
            options={filterOptions.keywords}
            setFieldValue={formik.setFieldValue}
            value={formik.values.withKeywords}
          />
          <InputLabel
            id="without-keywords-filter-label"
            sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
          >
            Without keyword
          </InputLabel>
          <KeywordMultiSelectDropdown
            ariaLabelledBy="without-keywords-filter-label"
            name="withoutKeywords"
            options={filterOptions.keywords}
            setFieldValue={formik.setFieldValue}
            value={formik.values.withoutKeywords}
          />
        </>
      ) : undefined}
      <InputLabel
        id="release-type-filter-label"
        sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
      >
        With release type
      </InputLabel>
      <ReleaseTypeMultiSelectDropdown
        ariaLabelledBy="release-type-filter-label"
        name="releaseType"
        setFieldValue={formik.setFieldValue}
        value={formik.values.releaseType}
      />
    </>
  );
};
